import React from 'react';
import useVehicleScheduleStore, { RENTAL_VEHICLE_ID } from '../stores/useVehicleScheduleStore';
import { useThemeStore } from '../stores/useThemeStore';
import { DAY_W, BAND_GAP, INFO_W } from './grid-constants';
import { DerivedVehicleBand, buildBandRuns } from './band-runs';
import { BandContextMenuArgs } from './VehicleBand';
import { VehicleScheduleEvent } from './types';

interface Props {
  /** A standalone show, or every arranged stop of one tour (bookend rule). */
  band: DerivedVehicleBand;
  dayIdx: Map<string, number>;
  windowStart: string;
  windowEnd: string;
  numDays: number;
  canEdit: boolean;
  onContext: (args: BandContextMenuArgs) => void;
  /** Left-click → open the event detail modal (parity with vehicle bands). */
  onOpenDetail: (eventId: string) => void;
  onHover: (event: VehicleScheduleEvent, x: number, y: number, box?: { top: number; bottom: number }) => void;
  onHoverEnd: () => void;
}

const TOUR_ACCENT = 'rgba(96,165,250,0.95)';
const TOUR_ACCENT_DAY = 'rgba(37,99,235,0.9)';

/** Color-coded by flavor so shipping vs drive-rental reads at a glance: cool teal
 *  for shipped freight, warm sand for a rented truck. Both stay low-saturation so
 *  they're clearly "off our owned fleet" beside the saturated per-vehicle hues.
 *  Day mode deepens the fill so it doesn't wash into a white grid. */
function fill(leg: boolean, day: boolean, shipping: boolean): React.CSSProperties {
  const h = shipping ? 190 : 30;
  if (day) {
    return leg
      ? { background: `hsl(${h} 24% 70%)`, color: '#1c2230', opacity: 0.95 }
      : { background: `hsl(${h} ${shipping ? 32 : 40}% 55%)`, color: '#10131a', opacity: 1 };
  }
  return leg
    ? { background: `hsl(${h} 20% 78%)`, color: '#1c2230', opacity: 0.9 }
    : { background: `hsl(${h} ${shipping ? 26 : 34}% 65%)`, color: '#10131a', opacity: 1 };
}

/**
 * A Shipping / Rentals band: a standalone show, or a whole tour as ONE connected
 * run, exactly the shape its vehicle-lane twin takes (see `deriveRentalLanes`). No
 * event_vehicle assignment; dragging the outer edges edits the first stop's
 * ship_out / last stop's ship_back, with the RENTAL_VEHICLE_ID sentinel so the
 * drag never triggers tour detection. Click opens the show's transport notes,
 * right-click the rental menu, both for the stop under the pointer.
 *
 * Flavor (shipped vs drive-rental) is per event, and a tour's stops could in
 * principle disagree. The band takes the FIRST stop's transport_mode for its color
 * and vocabulary, the same stop the Packing page treats as the tour's canonical
 * one; each show run's tooltip still names its own stop's flavor.
 */
const RentalBand: React.FC<Props> = ({ band, dayIdx, windowStart, windowEnd, numDays, canEdit, onContext, onOpenDetail, onHover, onHoverEnd }) => {
  const { events, isTour, tourName } = band;
  const first = events[0];
  const dayMode = useThemeStore(s => s.dayMode);
  const startDrag = useVehicleScheduleStore(s => s.startDrag);
  // Live drag for THIS band — the drag targets the first or last stop's edge.
  const drag = useVehicleScheduleStore(s =>
    s.drag && s.drag.vehicleId === RENTAL_VEHICLE_ID &&
    (s.drag.eventId === band.firstEventId || s.drag.eventId === band.lastEventId) ? s.drag : null
  );
  const dragActive = useVehicleScheduleStore(s => !!s.drag);

  let effLeave = band.baseLeave;
  let effReturn = band.baseReturn;
  if (drag && !drag.invalid) {
    if (drag.edge === 'left') effLeave = drag.previewDate;
    else effReturn = drag.previewDate;
    if (effReturn < effLeave) { effLeave = effReturn = drag.previewDate; }
  }

  const shipping = first.transport_mode === 'shipping';
  const runs = buildBandRuns(events, band.assignmentByEventId, effLeave, effReturn, shipping);
  if (!runs.length) return null;

  const clampIdx = (d: string): number => {
    if (d < windowStart) return 0;
    if (d > windowEnd) return numDays - 1;
    return dayIdx.get(d) ?? 0;
  };
  const leaveIdx = clampIdx(effLeave);
  const returnIdx = clampIdx(effReturn);

  const eventFor = (eventId: string) => events.find(e => e.id === eventId) ?? first;
  const kindLabel = (e: VehicleScheduleEvent) => (e.transport_mode === 'shipping' ? 'Shipping' : 'Drive rental');
  // The show run always shows the EVENT NAME (the transport note is supplementary,
  // in the tooltip and the detail modal, never overwriting the name).
  const tip = (e: VehicleScheduleEvent) =>
    `${kindLabel(e)} · ${e.name}${tourName ? `  ·  tour: ${tourName}` : ''}${e.transport_out_note ? ` — out: ${e.transport_out_note}` : ''}${e.transport_back_note ? ` · back: ${e.transport_back_note}` : ''}`;

  const openMenu = (e: VehicleScheduleEvent) => (ev: React.MouseEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    if (canEdit) onContext({ kind: 'rental', assignmentId: '', eventId: e.id, vehicleId: '', eventName: e.name, x: ev.clientX, y: ev.clientY });
  };
  const openDetail = (eventId: string) => (ev: React.MouseEvent) => {
    ev.stopPropagation();
    onOpenDetail(eventId);
  };

  const handlePointerDown = (edge: 'left' | 'right') => (e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    startDrag({
      vehicleId: RENTAL_VEHICLE_ID,
      eventId: edge === 'left' ? band.firstEventId : band.lastEventId,
      edge,
      evStart: band.firstEventDate,
      evEnd: band.lastEventEnd,
      anchorDate: edge === 'left' ? band.baseReturn : band.baseLeave,
      previewDate: edge === 'left' ? band.baseLeave : band.baseReturn,
      invalid: false,
    });
  };

  return (
    <div className={`group/band ${dragActive ? 'pointer-events-none' : ''}`}>
      {isTour && returnIdx >= leaveIdx && (
        <div
          className="absolute inset-y-1 rounded-lg pointer-events-none"
          style={{ left: leaveIdx * DAY_W + 1, width: (returnIdx - leaveIdx + 1) * DAY_W - 2, border: `1.5px solid ${dayMode ? TOUR_ACCENT_DAY : TOUR_ACCENT}`, background: dayMode ? 'rgba(37,99,235,0.06)' : 'rgba(96,165,250,0.07)' }}
        />
      )}

      {runs.map((run) => {
        const sIdx = clampIdx(run.start);
        const eIdx = clampIdx(run.end);
        if (eIdx < sIdx) return null;
        const left = sIdx * DAY_W + BAND_GAP;
        const width = (eIdx - sIdx + 1) * DAY_W - BAND_GAP * 2;
        const e = eventFor(run.eventId);
        return (
          <div
            key={`${run.kind}-${run.eventId}-${run.start}`}
            data-event-id={e.id}
            onClick={openDetail(e.id)}
            onContextMenu={openMenu(e)}
            onMouseEnter={(ev) => { const r = ev.currentTarget.getBoundingClientRect(); onHover(e, ev.clientX, ev.clientY, { top: r.top, bottom: r.bottom }); }}
            onMouseLeave={onHoverEnd}
            aria-label={`${kindLabel(e)}: ${e.name}, ${run.label}.`}
            className={`absolute inset-y-2 px-1 flex items-center justify-start cursor-pointer ${run.leg ? 'rounded' : 'rounded-md'}`}
            style={{ ...fill(run.leg, dayMode, shipping), left, width }}
            title={tip(e)}
          >
            <span
              className={`text-[11px] leading-tight truncate inline-block ${run.leg ? 'italic font-medium' : 'font-semibold'}`}
              style={{ position: 'sticky', left: INFO_W + BAND_GAP + 4, maxWidth: Math.max(0, width - 8) }}
            >
              {run.label}
            </span>
          </div>
        );
      })}

      {canEdit && (
        <>
          <div role="slider" aria-label="Drag to set rental start" onPointerDown={handlePointerDown('left')}
            className="absolute inset-y-2 w-1.5 cursor-ew-resize opacity-0 group-hover/band:opacity-100 bg-primary rounded-l transition-opacity"
            style={{ left: leaveIdx * DAY_W + BAND_GAP }} />
          <div role="slider" aria-label="Drag to set rental end" onPointerDown={handlePointerDown('right')}
            className="absolute inset-y-2 w-1.5 cursor-ew-resize opacity-0 group-hover/band:opacity-100 bg-primary rounded-r transition-opacity"
            style={{ left: (returnIdx + 1) * DAY_W - BAND_GAP - 6 }} />
        </>
      )}
    </div>
  );
};

export default RentalBand;
